import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Save, Send, Eye, CheckCircle2, XCircle, Trash2, Clock, Sparkles, ArrowRight, Mail, GitBranch, Undo2, Lock, Layers } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDMY, formatDMYTime } from "@/lib/date";
import { STATUS_STYLES, RevBadge, DownloadPdfButton } from "@/components/quotation/QuotationBits";
import { MarkSentDialog } from "@/components/quotation/MarkSentDialog";
import { UndoSentDialog } from "@/components/quotation/UndoSentDialog";
import { VersionHistory } from "@/components/quotation/VersionHistory";
import { SendEmailDialog } from "@/components/quotation/SendEmailDialog";
import { EmailLogPanel } from "@/components/quotation/EmailLogPanel";
import { ApproveOptionDialog } from "@/components/quotation/ApproveOptionDialog";
import { OptionCard } from "@/components/quotation/OptionCard";
import { BLANK_LINE } from "@/components/quotation/LineItemsEditor";
import { can } from "@/lib/perms";

const LETTERS = ["A", "B", "C"];
const OPT_KEYS = ["quote_type", "line_items", "payment_terms", "shipping_terms", "warranty_terms", "rental"];
const optionsFrom = (q) => [
  { label: q.option_label || "", ...Object.fromEntries(OPT_KEYS.map((k) => [k, q[k]])), quote_type: q.quote_type || "sale", line_items: q.line_items || [] },
  ...(q.additional_options || []),
];
const cleanLines = (items) => items.map((it) => ({ catalog_id: it.catalog_id || "", item_code: it.item_code || "", machine_model: it.machine_model, uom: it.uom || "PCS", quantity: Number(it.quantity), unit_price: Number(it.unit_price), discount_amount: Number(it.discount_amount) || 0 }));
const cleanRental = (o) => (o.rental && (o.quote_type === "rental" || o.quote_type === "rental_buy") ? {
  ...o.rental, lock_in_months: Number(o.rental.lock_in_months) || 36, deposit_months: Number(o.rental.deposit_months ?? 3),
  buy_amount: o.rental.buy_amount === "" || o.rental.buy_amount == null ? null : Number(o.rental.buy_amount),
} : null);

const TIMELINE_LABEL = {
  created: (e) => <>Created — {e.quotation_number}</>,
  status_change: (e) => <>Moved from <b>{e.from}</b> to <b>{e.to}</b>{e.option ? <> · client accepted <b>Option {e.option}</b></> : null}</>,
  sent: (e) => <>Emailed to <b>{e.to}</b></>,
  marked_sent: (e) => <>Marked as sent via <b>{e.via}</b> on {formatDMY(e.sent_date)}{e.note ? ` — ${e.note}` : ""}</>,
  sent_undone: (e) => <>Sent undone — {e.reason}</>,
  superseded: (e) => <>Superseded by <b>{e.by}</b></>,
  supersede_undone: () => <>Current again (newer version returned to Draft)</>,
  revision_created: (e) => <>Revision {e.revision} created from {e.from_number}</>,
  revised: (e) => <>Revision {e.revision} created</>,
  expired: (e) => <>Auto-expired (valid until {e.valid_until})</>,
};


const SHIP_FIELDS = [
  ["company_name", "Company name"], ["contact_person", "Contact person"], ["address", "Address"],
  ["city", "City"], ["state", "State"], ["pincode", "PIN"], ["email", "Email"], ["phone", "Phone"],
];

const BILL_FIELDS = [
  ["company_name", "Company name"], ["address", "Address"], ["city", "City"], ["state", "State"],
  ["pincode", "PIN"], ["contact_person", "Contact person"], ["email", "Email"], ["phone", "Phone"],
];

function BillTo({ editable, billTo, onChange }) {
  const noAddress = !(billTo.address || "").trim();
  const csp = [billTo.city, billTo.state, billTo.pincode].filter((v) => (v || "").trim()).join(", ");
  const ep = [billTo.email, billTo.phone].filter((v) => (v || "").trim()).join(" | ");
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="bill-to-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Recipient / Bill to</div>
        {noAddress && editable && <span className="text-[11px] font-medium text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5" data-testid="bill-to-add-address">Add address</span>}
      </div>
      {editable ? (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {BILL_FIELDS.map(([k, label]) => (
            <Input key={k} value={billTo[k] || ""} placeholder={label} onChange={(e) => onChange(k, e.target.value)}
              className={`${k === "company_name" || k === "address" ? "sm:col-span-3" : ""} ${k === "address" && noAddress ? "border-amber-300 bg-amber-50/40" : ""}`}
              data-testid={`billto-${k}-input`} />
          ))}
          <div className="sm:col-span-3 text-[11px] text-slate-500">Changes here apply to this quotation only. The client record isn't changed.</div>
        </div>
      ) : (
        <div className="text-sm text-slate-700 space-y-0.5" data-testid="bill-to-readonly">
          <div className="font-semibold text-slate-900">{billTo.company_name || "—"}</div>
          {billTo.address && <div className="whitespace-pre-line">{billTo.address}</div>}
          {csp && <div>{csp}</div>}
          {billTo.contact_person && <div>{billTo.contact_person}</div>}
          {ep && <div className="text-xs text-slate-500">{ep}</div>}
        </div>
      )}
    </div>
  );
}

function ProjectShipTo({ editable, projectName, shipTo, onProject, onShip }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4" data-testid="project-shipto-card">
      <Label className="text-xs text-slate-500">Project (optional)</Label>
      <Input value={projectName} disabled={!editable} onChange={(e) => onProject(e.target.value)} className="mt-1" placeholder="e.g. Phase 2 mall cleaning" data-testid="project-name-input" />
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mt-4 mb-1">Ship to (if different)</div>
      <div className="text-[11px] text-slate-500 mb-2">Leave blank to print "Same as Bill To".</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {SHIP_FIELDS.map(([k, label]) => (
          <Input key={k} value={shipTo[k] || ""} disabled={!editable} placeholder={label} onChange={(e) => onShip(k, e.target.value)}
            className={k === "address" ? "sm:col-span-2" : ""} data-testid={`shipto-${k}-input`} />
        ))}
      </div>
    </div>
  );
}


export default function QuotationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user: me } = useAuth();
  const [q, setQ] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [options, setOptions] = useState([]);
  const [layout, setLayout] = useState("comparison");
  const [approveOpen, setApproveOpen] = useState(false);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [gstApplicable, setGstApplicable] = useState(false);
  const [gstRate, setGstRate] = useState(18);
  const [projectName, setProjectName] = useState("");
  const [shipTo, setShipTo] = useState({});
  const [billTo, setBillTo] = useState({});
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pendingReserved, setPendingReserved] = useState(null); // list of reserved units awaiting decision
  const [conversionSelling, setConversionSelling] = useState(null); // unit to sell via SellDialog

  const load = async () => {
    try {
      const [r, c] = await Promise.all([api.get(`/quotations/${id}`), api.get("/catalog")]);
      setQ(r.data);
      setOptions(optionsFrom(r.data));
      setLayout(r.data.options_layout || "comparison");
      setValidUntil(r.data.valid_until || "");
      setNotes(r.data.notes || "");
      setGstApplicable(!!r.data.gst_applicable);
      setGstRate(Number(r.data.gst_rate) || 18);
      setProjectName(r.data.project_name || "");
      setShipTo(r.data.ship_to || {});
      setBillTo(r.data.bill_to || {});
      setCatalog(c.data);
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); nav("/quotations"); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const isAdmin = can(me, "system_admin");
  const canEditQ = can(me, "quotations", "edit");
  const canDeleteQ = can(me, "quotations", "admin");
  const editable = q?.status === "Draft" && canEditQ;
  const [markSentOpen, setMarkSentOpen] = useState(false);
  const [undoOpen, setUndoOpen] = useState(false);
  const [sendOpen, setSendOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const setOption = (i, o) => { setOptions((prev) => prev.map((x, idx) => (idx === i ? o : x))); setDirty(true); };
  const addOption = () => {
    const d = q.rental_defaults || {};
    setOptions((prev) => [...prev, { label: "", quote_type: "sale", line_items: [{ ...BLANK_LINE }], payment_terms: d.sale_payment_terms, shipping_terms: d.shipping_terms, warranty_terms: d.sale_warranty_terms, rental: null }]);
    setDirty(true);
  };
  const removeOption = (i) => { setOptions((prev) => prev.filter((_, idx) => idx !== i)); setDirty(true); };

  const save = async () => {
    if (!q) return;
    if (!validUntil) { toast.error("Set valid-until date"); return; }
    if (options.some((o) => !o.line_items.length || o.line_items.some((it) => !(it.machine_model || "").trim()))) { toast.error("Every line needs a model"); return; }
    const [a, ...rest] = options;
    setSaving(true);
    try {
      await api.put(`/quotations/${q.id}`, {
        lead_id: q.lead_id,
        valid_until: validUntil,
        notes,
        discount_amount: 0,
        quote_type: a.quote_type,
        rental: cleanRental(a),
        option_label: a.label || "",
        options_layout: layout,
        additional_options: rest.map((o) => ({ ...(o.id ? { id: o.id } : {}), label: o.label || "", quote_type: o.quote_type, payment_terms: o.payment_terms, shipping_terms: o.shipping_terms, warranty_terms: o.warranty_terms, rental: cleanRental(o), line_items: cleanLines(o.line_items) })),
        payment_terms: a.payment_terms,
        shipping_terms: a.shipping_terms,
        warranty_terms: a.warranty_terms,
        gst_applicable: gstApplicable,
        gst_rate: Number(gstRate) || 0,
        project_name: projectName,
        ship_to: Object.values(shipTo).some((v) => (v || "").trim()) ? shipTo : null,
        bill_to: billTo,
        line_items: cleanLines(a.line_items),
      });
      toast.success("Saved");
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const revise = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/quotations/${q.id}/revise`);
      toast.success(`${r.data.display_number} created as a draft`);
      nav(`/quotations/${r.data.id}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };


  const changeStatus = async (status, accepted_option) => {
    if (!q) return;
    try {
      const r = await api.patch(`/quotations/${q.id}/status`, { status, ...(accepted_option ? { accepted_option } : {}) });
      setApproveOpen(false);
      toast.success(`Marked ${status}${accepted_option ? ` · Option ${accepted_option}` : ""}`);
      // If this Approve returned reserved units, prompt the user.
      if (status === "Approved" && Array.isArray(r.data.pending_reserved_units) && r.data.pending_reserved_units.length) {
        setPendingReserved(r.data.pending_reserved_units);
      }
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const openPrintable = async () => {
    // The /view endpoint returns HTML; open it in a new tab with auth via query.
    const token = localStorage.getItem("token");
    const base = process.env.REACT_APP_BACKEND_URL;
    // fetch HTML via authenticated axios, then blob-open (no cookie/token in URL)
    try {
      const resp = await fetch(`${base}/api/quotations/${q.id}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const html = await resp.text();
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
    } catch (e) { toast.error("Could not open printable view"); }
  };

  const remove = async () => {
    if (!q) return;
    if (!window.confirm("Delete this draft quotation?")) return;
    try { await api.delete(`/quotations/${q.id}`); toast.success("Deleted"); nav("/quotations"); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  if (!q) return <div className="text-slate-500 text-sm">Loading…</div>;

  return (
    <div data-testid="quotation-detail-page">
      <button onClick={() => nav("/quotations")} className="text-sm text-slate-500 hover:text-indigo-600 mb-3 inline-flex items-center gap-1" data-testid="back-to-quotations"><ArrowLeft className="w-3.5 h-3.5" /> Back to quotations</button>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div className="min-w-0">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Quotation</div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 font-mono truncate flex items-center gap-2" data-testid="quotation-number">
            {q.quotation_number} <RevBadge revision={q.revision} testId="detail-rev-badge" />
          </h1>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <Badge variant="outline" className={STATUS_STYLES[q.status]} data-testid="status-badge">{q.status}</Badge>
            {q.status === "Approved" && q.accepted_option && <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200" data-testid="accepted-option-badge">Accepted: Option {q.accepted_option}</Badge>}
            {q.sent_at && <span className="text-xs text-slate-500" data-testid="sent-info"><Mail className="w-3 h-3 inline mr-0.5" /> Sent {formatDMY(q.sent_at)}{q.sent_via ? ` via ${q.sent_via}` : ""}{q.sent_by?.email ? ` · by ${q.sent_by.name || q.sent_by.email}` : ""}</span>}
            {!canEditQ && <span className="text-xs text-slate-400 inline-flex items-center gap-1" data-testid="view-only-label"><Eye className="w-3 h-3" /> View only</span>}
            {canEditQ && !editable && q.status !== "Superseded" && <span className="text-xs text-slate-400 inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Locked</span>}
          </div>
        </div>
        <div className="flex flex-wrap gap-2 md:justify-end">
          <DownloadPdfButton quotation={q} testId="download-pdf-btn" />
          <Button variant="outline" onClick={openPrintable} data-testid="print-btn"><Eye className="w-4 h-4 mr-1.5" /> Preview</Button>
          {canEditQ && q.status === "Draft" && <Button onClick={() => setMarkSentOpen(true)} disabled={dirty} title={dirty ? "Save your changes first" : ""} className="bg-indigo-600 hover:bg-indigo-700" data-testid="mark-sent-btn"><Send className="w-4 h-4 mr-1.5" /> Mark as Sent</Button>}
          {canEditQ && q.status === "Draft" && q.email_sending_enabled && !q.newer_version_id && <Button onClick={() => setSendOpen(true)} disabled={dirty} title={dirty ? "Save your changes first" : ""} className="bg-emerald-600 hover:bg-emerald-700" data-testid="send-email-btn"><Mail className="w-4 h-4 mr-1.5" /> Send</Button>}
          {canEditQ && ["Sent", "Opened", "Expired"].includes(q.status) && !q.newer_version_id && (
            <Button variant="outline" onClick={revise} disabled={busy} className="text-violet-700 border-violet-200 hover:bg-violet-50" data-testid="create-revision-btn"><GitBranch className="w-4 h-4 mr-1.5" /> Create Revision</Button>
          )}
          {isAdmin && ["Sent", "Opened", "Expired"].includes(q.status) && !q.newer_version_id && (
            <Button variant="outline" onClick={() => setUndoOpen(true)} className="text-rose-600 border-rose-200" data-testid="undo-sent-btn"><Undo2 className="w-4 h-4 mr-1.5" /> Undo Sent</Button>
          )}
          {canEditQ && (q.status === "Sent" || q.status === "Opened") && (
            <>
              <Button onClick={() => ((q.additional_options || []).length ? setApproveOpen(true) : changeStatus("Approved"))} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn"><CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve</Button>
              <Button variant="outline" onClick={() => changeStatus("Rejected")} className="text-red-600 border-red-200" data-testid="reject-btn"><XCircle className="w-4 h-4 mr-1.5" /> Reject</Button>
            </>
          )}
          {canDeleteQ && q.status === "Draft" && <Button variant="outline" onClick={remove} className="text-red-600 border-red-200" data-testid="delete-draft-btn"><Trash2 className="w-4 h-4" /></Button>}
        </div>
      </div>

      {q.status === "Superseded" && (
        <div className="mb-4 rounded-lg border border-zinc-300 bg-zinc-50 px-4 py-3 text-sm text-zinc-700 flex flex-col sm:flex-row sm:items-center gap-2" data-testid="superseded-banner">
          <Layers className="w-4 h-4 shrink-0" />
          <span className="flex-1">This version has been <b>superseded</b> by a newer revision. It stays here for reference and can still be downloaded, but it can't be sent or approved.</span>
          {q.newer_version_id && <Button size="sm" variant="outline" onClick={() => nav(`/quotations/${q.newer_version_id}`)} data-testid="open-latest-version-btn">Open latest version</Button>}
        </div>
      )}
      {q.status === "Draft" && q.revision > 0 && (
        <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50 px-4 py-3 text-sm text-violet-800" data-testid="revision-draft-banner">
          Draft revision {q.revision}. Edit it freely. When you mark it as sent, the previous version becomes <b>Superseded</b>. The lead's stage and SLA clock are not changed.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-4">
        {/* Body */}
        <div className="space-y-4">
          {/* Lead card */}
          <BillTo editable={editable} billTo={billTo} onChange={(k, v) => { setBillTo((p) => ({ ...p, [k]: v })); setDirty(true); }} />

          {/* Options (one card per option; option A is the quotation itself) */}
          {options.map((o, i) => (
            <OptionCard key={o.id || i} o={o} letter={LETTERS[i]} multi={options.length > 1} onChange={(x) => setOption(i, x)} onRemove={() => removeOption(i)}
              editable={editable} catalog={catalog} defaults={q.rental_defaults || {}} isAdmin={isAdmin} />
          ))}
          {(editable || options.length > 1) && (
            <div className="bg-white border border-dashed border-slate-300 rounded-lg p-3 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="options-bar">
              <div className="text-sm text-slate-600 flex-1">{options.length > 1 ? `${options.length} options on one quotation. Number, Bill To / Ship To, validity and T&C are shared.` : "Offer the client up to 3 options (e.g. Rental, Outright buy, Rental with buy option) on one PDF."}</div>
              {options.length > 1 && (
                <Select value={layout} onValueChange={(v) => { setLayout(v); setDirty(true); }} disabled={!editable}>
                  <SelectTrigger className="sm:w-72" data-testid="options-layout-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="comparison" data-testid="layout-comparison">PDF: comparison table, then details</SelectItem>
                    <SelectItem value="stacked" data-testid="layout-stacked">PDF: options stacked, no comparison</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {editable && options.length < 3 && <Button size="sm" variant="outline" onClick={addOption} data-testid="add-option-btn"><Plus className="w-3 h-3 mr-1" /> Add option</Button>}
            </div>
          )}

          {/* Project & Ship To */}
          <ProjectShipTo editable={editable} projectName={projectName} shipTo={shipTo}
            onProject={(v) => { setProjectName(v); setDirty(true); }} onShip={(k, v) => { setShipTo((s) => ({ ...s, [k]: v })); setDirty(true); }} />

          {/* GST (shared by all options) */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">GST</div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <label className="text-sm text-slate-600 flex items-center gap-2">
                  <input type="checkbox" checked={gstApplicable} disabled={!editable} onChange={(e) => { setGstApplicable(e.target.checked); setDirty(true); }} data-testid="gst-toggle" />
                  GST applicable
                </label>
                <div className="flex items-center gap-1 text-sm">
                  <span className="text-slate-500">Rate %</span>
                  <Input type="number" min="0" step="0.5" value={gstRate} disabled={!editable || !gstApplicable} onChange={(e) => { setGstRate(e.target.value); setDirty(true); }} className="w-20 text-right" data-testid="gst-rate" />
                </div>
              </div>
              <div className="text-[11px] italic text-slate-500" data-testid="gst-note">{options.length > 1 || options.some((o) => o.quote_type !== "sale") ? "All prices are exclusive of GST." : "Prices mentioned above are exclusive of GST."}{gstApplicable ? ` GST @ ${gstRate}% applicable extra.` : ""}</div>
            </div>
          </div>

          {/* Notes */}
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Notes</div>
            <textarea value={notes} onChange={(e) => { setNotes(e.target.value); setDirty(true); }} disabled={!editable} rows={3} className="w-full border border-slate-200 rounded-md p-2 text-sm disabled:bg-slate-50" data-testid="quotation-notes" />
          </div>

          {editable && (
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={load} disabled={!dirty}>Discard</Button>
              <Button onClick={save} disabled={!dirty || saving} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-btn"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving..." : "Save"}</Button>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          <VersionHistory quotation={q} />
          <EmailLogPanel quotation={q} />
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Details</div>
            <div className="space-y-2 text-sm">
              <div>
                <div className="text-xs text-slate-500">Valid until</div>
                <Input type="date" value={validUntil} disabled={!editable} onChange={(e) => { setValidUntil(e.target.value); setDirty(true); }} className="mt-1" data-testid="valid-until-input" />
              </div>
              <div className="pt-2 border-t border-slate-100">
                <div className="text-xs text-slate-500">Created by</div>
                <div>{q.created_by || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Created at</div>
                <div>{q.created_at ? formatDMYTime(q.created_at) : "—"}</div>
              </div>
              {q.responded_at && (
                <div>
                  <div className="text-xs text-slate-500">Responded at</div>
                  <div>{formatDMYTime(q.responded_at)}</div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Timeline</div>
            <ol className="relative border-l border-slate-200 pl-4 space-y-3">
              {(q.timeline || []).map((e, idx) => (
                <li key={idx} className="relative">
                  <span className={`absolute -left-[25px] w-4 h-4 rounded-full flex items-center justify-center border-2 border-white ${
                    e.type === "created" ? "bg-slate-400" :
                    e.type === "status_change" ? "bg-indigo-500" :
                    e.type === "sent" || e.type === "marked_sent" ? "bg-sky-500" :
                    e.type === "revision_created" || e.type === "revised" ? "bg-violet-500" :
                    e.type === "sent_undone" ? "bg-rose-500" :
                    e.type === "expired" ? "bg-amber-500" : "bg-slate-400"
                  }`}>
                    {e.type === "created" && <Sparkles className="w-2 h-2 text-white" />}
                    {e.type === "status_change" && <ArrowRight className="w-2 h-2 text-white" />}
                    {(e.type === "sent" || e.type === "marked_sent") && <Send className="w-2 h-2 text-white" />}
                    {e.type === "expired" && <Clock className="w-2 h-2 text-white" />}
                  </span>
                  <div className="text-sm text-slate-900">{(TIMELINE_LABEL[e.type] || (() => e.type))(e)}</div>
                  <div className="text-xs text-slate-500">{e.actor} · {formatDMYTime(e.at)}</div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      {approveOpen && <ApproveOptionDialog options={optionsFrom(q).map((o, i) => ({ ...o, letter: LETTERS[i] }))} open={approveOpen} onOpenChange={setApproveOpen} onConfirm={(letter) => changeStatus("Approved", letter)} />}
      <MarkSentDialog quotation={q} open={markSentOpen} onOpenChange={setMarkSentOpen} onDone={() => { setMarkSentOpen(false); load(); }} />
      {sendOpen && <SendEmailDialog quotation={q} open={sendOpen} onOpenChange={setSendOpen} onSent={() => { setSendOpen(false); load(); }} />}
      <UndoSentDialog quotation={q} open={undoOpen} onOpenChange={setUndoOpen} onDone={() => { setUndoOpen(false); load(); }} />

      {/* Deal-closed prompt: convert reserved units to Sold-Dispatched */}
      <PendingReservedDialog
        units={pendingReserved}
        quotationNumber={q?.quotation_number}
        onClose={() => setPendingReserved(null)}
        onPickSell={(unit) => { setConversionSelling(unit); }}
        onReleaseOne={async (unitId) => {
          try {
            await api.post(`/inventory/${unitId}/release`);
            toast.success("Reservation released");
            setPendingReserved((prev) => (prev || []).filter((u) => u.id !== unitId));
          } catch (err) { toast.error(formatApiError(err)); }
        }}
      />
      <ConvertReservationToSaleDialog
        unit={conversionSelling}
        quotationLead={q?.lead}
        onClose={() => setConversionSelling(null)}
        onSold={(soldUnitId) => {
          setConversionSelling(null);
          setPendingReserved((prev) => (prev || []).filter((u) => u.id !== soldUnitId));
          toast.success("Unit marked as Sold-Dispatched");
        }}
      />
    </div>
  );
}

// ---------- Prompt: reserved units awaiting decision on Approve ----------
function PendingReservedDialog({ units, quotationNumber, onClose, onPickSell, onReleaseOne }) {
  if (!units || units.length === 0) return null;
  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="pending-reserved-dialog">
        <DialogHeader><DialogTitle>Deal closed — convert reservations?</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-slate-600">
            Quotation <span className="font-mono">{quotationNumber}</span> has <b>{units.length}</b> reserved unit{units.length > 1 ? "s" : ""} awaiting a decision.
            Pick which one is actually being delivered — you can release the rest.
          </p>
          <div className="rounded-md border border-slate-200 divide-y">
            {units.map((u) => (
              <div key={u.id} className="p-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2" data-testid={`pending-reserved-row-${u.id}`}>
                <div>
                  <div className="font-mono text-sm text-slate-900">{u.serial_number}</div>
                  <div className="text-xs text-slate-500">{u.nickname || u.location || ""}</div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => onReleaseOne?.(u.id)}
                    className="text-xs px-3 py-1 rounded border border-slate-200 text-slate-700 hover:bg-slate-50"
                    data-testid={`pending-release-btn-${u.id}`}
                  >Release</button>
                  <button
                    onClick={() => onPickSell?.(u)}
                    className="text-xs px-3 py-1 rounded bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid={`pending-sell-btn-${u.id}`}
                  >Mark Sold-Dispatched</button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex justify-end pt-2">
            <button onClick={onClose} className="text-xs text-slate-500 hover:text-slate-800">Decide later</button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Convert one reservation to Sold-Dispatched ----------
function ConvertReservationToSaleDialog({ unit, quotationLead, onClose, onSold }) {
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("");
  const [addressId, setAddressId] = useState("__billing__");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!unit) return;
    api.get("/clients").then((r) => {
      setClients(r.data || []);
      // Pre-select the lead's client if it's already converted
      const match = (r.data || []).find((c) => c.company_name && quotationLead?.company_name && c.company_name.toLowerCase() === quotationLead.company_name.toLowerCase());
      if (match) setClientId(match.id);
    }).catch(() => {});
  }, [unit?.id, quotationLead?.company_name]);

  if (!unit) return null;

  const client = clients.find((c) => c.id === clientId);
  const addresses = client?.shipping_addresses || [];

  const submit = async (e) => {
    e.preventDefault();
    if (!clientId) { toast.error("Pick the client"); return; }
    setSaving(true);
    try {
      await api.patch(`/inventory/${unit.id}/sell`, { client_id: clientId, address_id: addressId });
      onSold?.(unit.id);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!unit} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-md" data-testid="convert-to-sale-dialog">
        <DialogHeader><DialogTitle>Mark {unit.serial_number} as Sold-Dispatched</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3 text-sm">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Client *</label>
            <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={clientId} onChange={(e) => setClientId(e.target.value)} data-testid="convert-client-select">
              <option value="">— Select client —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.company_name}</option>)}
            </select>
          </div>
          {client && (
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Delivery address</label>
              <select className="mt-1 w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={addressId} onChange={(e) => setAddressId(e.target.value)} data-testid="convert-address-select">
                <option value="__billing__">Billing — {(client.billing_address || "").slice(0, 60)}</option>
                {addresses.map((a) => <option key={a.id} value={a.id}>{a.label || a.line || "Address"}</option>)}
              </select>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="text-xs px-3 py-1.5 rounded border border-slate-200 text-slate-700">Cancel</button>
            <button type="submit" disabled={saving} className="text-xs px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-700" data-testid="convert-confirm-btn">
              {saving ? "Marking…" : "Confirm sale"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
